// Server-side password change. Verifies the current password, requires a
// fresh TOTP code when the user is enrolled, then updates the password via
// the Auth Admin API. Returns a serializable result (no exceptions escape
// across the RPC boundary).
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { Secret, TOTP } from "otpauth";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

const ISSUER = "Want To Buy";

const Input = z.object({
  current_password: z.string().min(1),
  new_password: z.string().min(8),
  totp_code: z.string().optional(),
});

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export const changePassword = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => Input.parse(i))
  .handler(async ({ data, context }) => {
    const { userId, claims } = context;
    const email = (claims.email as string | undefined) ?? null;
    if (!email) return { ok: false as const, error: "No email on session" };

    // Re-verify the current password via a fresh sign-in on a throwaway
    // client (no persistence, no impact on the user's current session).
    const verifier = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { error: pwErr } = await verifier.auth.signInWithPassword({
      email,
      password: data.current_password,
    });
    if (pwErr) return { ok: false as const, error: "Current password is incorrect" };

    // If TOTP is enrolled, require a fresh code. Read via service role —
    // `user_totp_factors` has all access revoked from the `authenticated`
    // role, so the user JWT client would silently return null and bypass 2FA.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: factor, error: factorErr } = await supabaseAdmin
      .from("user_totp_factors")
      .select("secret, verified, backup_codes")
      .eq("user_id", userId)
      .maybeSingle();
    if (factorErr) {
      return { ok: false as const, error: "Could not verify 2FA status. Please try again." };
    }
    if (factor?.verified) {
      const code = (data.totp_code ?? "").trim();
      if (!code) return { ok: false as const, error: "Enter your 2FA code" };
      let totpOk = false;
      if (/^\d{6}$/.test(code)) {
        const totp = new TOTP({
          issuer: ISSUER,
          algorithm: "SHA1",
          digits: 6,
          period: 30,
          secret: Secret.fromBase32(factor.secret),
        });
        totpOk = totp.validate({ token: code, window: 1 }) !== null;
      } else {
        // Backup code path
        const hashed = await sha256Hex(code.toUpperCase());
        if (factor.backup_codes?.includes(hashed)) {
          totpOk = true;
          const remaining = (factor.backup_codes ?? []).filter((c) => c !== hashed);
          const { error: consumeErr } = await supabaseAdmin
            .from("user_totp_factors")
            .update({ backup_codes: remaining })
            .eq("user_id", userId);
          if (consumeErr) {
            return { ok: false as const, error: "Could not consume backup code. Please try again." };
          }
        }
      }
      if (!totpOk) return { ok: false as const, error: "Invalid 2FA code" };
    }

    // Update via Auth Admin (service role).
    const { error: updErr } = await supabaseAdmin.auth.admin.updateUserById(userId, {
      password: data.new_password,
    });
    if (updErr) return { ok: false as const, error: updErr.message };
    return { ok: true as const };
  });
